import { DynamicContainer, FormGroup, FormSection, TagInput } from '@hai/ui-react';
import { FormikProps } from 'formik';
import { equals } from 'ramda';
import React, { MutableRefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormSelect, SelectOptions } from 'src/js/component/base/form/form-select';
import { FormSwitch } from 'src/js/component/base/form/form-switch';
import { FormTextfield } from 'src/js/component/base/form/form-textfield';
import { FormChangeEvent } from 'src/js/util/global-type';

import {
  MetadataTypeEnum,
  MetadataViewModel,
  UasIds,
  UasMinIds,
  UasSecurityIds,
  UasSecurityMinIds,
  UasSecurityTable,
  UasTable,
} from './metadata-models';
import { generateDataAuto } from 'src/js/util/automation';

export const getUASSelection = (uasTags: number[]) => {
  let selection = '';

  if (equals(uasTags, UasMinIds)) {
    selection = 'minimum';
  } else if (equals(uasTags, UasIds)) {
    selection = 'all';
  } else if (uasTags.length === 0) {
    selection = 'none';
  } else {
    selection = 'custom';
  }

  return selection;
};

export const getUASSecuritySelection = (uasSecurityTags: number[]) => {
  let selection = '';

  if (equals(uasSecurityTags, UasSecurityMinIds)) {
    selection = 'minimum';
  } else if (equals(uasSecurityTags, UasSecurityIds)) {
    selection = 'all';
  } else if (uasSecurityTags.length === 0) {
    selection = 'none';
  } else {
    selection = 'custom';
  }

  return selection;
};

interface Props {
  values: MetadataViewModel;
  formRef: MutableRefObject<FormikProps<MetadataViewModel>>;
}

type TagInputList = {
  text: string;
  active: boolean;
}[];

export const MetadataSecurityInsertion: React.FunctionComponent<Props> = ({ values, formRef }) => {
  const { t } = useTranslation();

  const [uasSelection, setUasSelection] = useState<string>();
  const [uasTags, setUasTags] = useState<TagInputList>([]);
  const [uasSecuritySelection, setUasSecuritySelection] = useState<string>();
  const [uasSecurityTags, setUasSecurityTags] = useState<TagInputList>([]);
  const [securityTagsDisabled, setSecurityTagsDisabled] = useState<boolean>(false);

  const uasOptions = (selection: string): SelectOptions => {
    const custom = selection === 'custom' ? [{ value: 'custom', label: t('metadata.settings.uas.custom') }] : [];

    return custom.concat([
      { value: 'none', label: t('metadata.settings.uas.none') },
      { value: 'minimum', label: t('metadata.settings.uas.minimum') },
      { value: 'all', label: t('metadata.settings.uas.all') },
    ]);
  };

  const updateUasTags = (tags: number[]) => {
    setUasTags(
      UasTable.map((item) => ({
        text: item.text,
        active: tags.includes(item.id),
      })),
    );
  };

  const updateUasSecurityTags = (tags: number[]) => {
    setUasSecurityTags(
      UasSecurityTable.map((item) => ({
        text: item.text,
        active: tags.includes(item.id),
      })),
    );
  };

  const handleChangeUASSelection = (formEvent: FormChangeEvent) => {
    const { value } = formEvent.target;

    if (value !== 'custom') {
      let tags: number[] = [];
      switch (value) {
        case 'none':
          break;
        case 'minimum':
          tags = UasMinIds;
          break;
        case 'all':
          tags = UasIds;
          break;
      }

      formRef.current.setFieldValue('uasTags', tags, true);
      formRef.current.setFieldTouched('uasTags');

      updateUasTags(tags);
    }
  };

  const handleChangeUASSecuritySelection = (formEvent: FormChangeEvent) => {
    const { value } = formEvent.target;

    if (value !== 'custom') {
      let tags: number[] = [];
      switch (value) {
        case 'none':
          break;
        case 'minimum':
          tags = UasSecurityMinIds;
          break;
        case 'all':
          tags = UasSecurityIds;
          break;
      }

      formRef.current.setFieldValue('uasSecurityTags', tags, true);
      formRef.current.setFieldTouched('uasSecurityTags');
      updateUasSecurityTags(tags);
    }
  };

  const updateUASSelection = () => {
    const selection = getUASSelection(values.uasTags);
    setUasSelection(selection);
    formRef.current.setFieldValue('uasSelection', selection, false);
  };

  const updateUASSecuritySelection = () => {
    const selection = getUASSecuritySelection(values.uasSecurityTags);
    setUasSecuritySelection(selection);
    formRef.current.setFieldValue('uasSecuritySelection', selection, false);
  };

  const handleChangeTags = (tags: any[]) => {
    const tagsIds = tags.filter((tag) => tag.active).map(({ text }) => parseInt(text.replace(/(\d+) - .*/, '$1')));
    formRef.current.setFieldValue('uasTags', tagsIds, true);
    formRef.current.setFieldTouched('uasTags');
  };

  const handleChangeSecurityTags = (tags: any[]) => {
    const tagsIds = tags.filter((tag) => tag.active).map(({ text }) => parseInt(text.replace(/(\d+) - .*/, '$1')));
    formRef.current.setFieldValue('uasSecurityTags', tagsIds, true);
    formRef.current.setFieldTouched('uasSecurityTags');
  };

  const updateFilteringSecurity = () => {
    const securityLocalSet = !values.klvFilteringEnabled || values.uasTags.includes(48);
    setSecurityTagsDisabled(!securityLocalSet);
    if (values.klvFilteringSecurityEnabled && !securityLocalSet) {
      formRef.current.setFieldValue('klvFilteringSecurityEnabled', false);
      formRef.current.setFieldTouched('klvFilteringSecurityEnabled');
    }
  };

  useEffect(() => {
    updateUASSelection();
    updateUASSecuritySelection();
    updateFilteringSecurity();
  }, [values.uasTags, values.uasSecurityTags]);

  useEffect(() => {
    updateUasTags(values.uasTags);
    updateUasSecurityTags(values.uasSecurityTags);
  }, []);

  const handleChangeKlvFiltering = (checked: boolean) => {
    if (checked) {
      if (values.uasTags.length === 0) {
        setUasSelection('minimum');
        formRef.current.setFieldValue('uasTags', UasMinIds);
        updateUasTags(UasMinIds);
      }
    } else {
      formRef.current.setFieldValue('uasTags', []);
      updateUasTags([]);
    }
  };

  const handleChangeKlvFilteringSecurity = (checked: boolean) => {
    if (checked) {
      if (values.uasSecurityTags.length === 0) {
        setUasSecuritySelection('minimum');
        formRef.current.setFieldValue('uasSecurityTags', UasSecurityMinIds);
        updateUasSecurityTags(UasSecurityMinIds);
      }
    } else {
      formRef.current.setFieldValue('uasSecurityTags', []);
      updateUasSecurityTags([]);
    }
  };

  const securityClassificationOptions: SelectOptions = [
    { value: 1, label: t('metadata.settings.security.unclassified') },
    { value: 2, label: t('metadata.settings.security.restricted') },
    { value: 3, label: t('metadata.settings.security.confidential') },
    { value: 4, label: t('metadata.settings.security.secret') },
    { value: 5, label: t('metadata.settings.security.topSecret') },
  ];

  const countryCodingOptions: SelectOptions = [{ value: 'ISO 3166-1 alpha-3', label: 'ISO 3166-1 alpha-3' }];

  return (
    <>
      {values.type === MetadataTypeEnum.INPUT && (
        <>
          <FormSection className="hai-mb-6" title={t('metadata.settings.klvFiltering')}>
            <DynamicContainer>
              <FormSwitch
                className="hai-mb-4"
                name="klvFilteringEnabled"
                switchLabel={t('metadata.settings.klvFiltering')}
                onChange={handleChangeKlvFiltering}
              />
            </DynamicContainer>
            {values.klvFilteringEnabled && (
              <>
                <DynamicContainer minColumns={3}>
                  <FormSelect
                    name="uasSelection"
                    title={t('metadata.settings.includedTags')}
                    defaultValue={uasSelection}
                    options={uasOptions(uasSelection)}
                    onChange={handleChangeUASSelection}
                  />
                </DynamicContainer>
                <DynamicContainer maxColumns={1} className="hai-mb-6">
                  <FormGroup>
                    <TagInput
                      data-auto={generateDataAuto('tag_container', 'uas klv tag filtering')}
                      tags={uasTags}
                      onChange={handleChangeTags}
                      noneAddedText=""
                      placeholder={t('general.search')}
                    />
                  </FormGroup>
                </DynamicContainer>
              </>
            )}
          </FormSection>
          <FormSection className="hai-mb-6" title={t('metadata.settings.klvSecurityFiltering')}>
            <DynamicContainer>
              <FormSwitch
                className="hai-mb-4"
                name="klvFilteringSecurityEnabled"
                disabled={securityTagsDisabled}
                switchLabel={t('metadata.settings.klvSecurityFiltering')}
                onChange={handleChangeKlvFilteringSecurity}
              />
            </DynamicContainer>
            {values.klvFilteringSecurityEnabled && (
              <>
                <DynamicContainer minColumns={3}>
                  <FormSelect
                    name="uasSecuritySelection"
                    title={t('metadata.settings.includedSecurityTags')}
                    defaultValue={uasSecuritySelection}
                    options={uasOptions(uasSecuritySelection)}
                    onChange={handleChangeUASSecuritySelection}
                  />
                </DynamicContainer>
                <DynamicContainer maxColumns={1} className="hai-mb-6">
                  <FormGroup>
                    <TagInput
                      data-auto={generateDataAuto('tag_container', 'uas klv security tag filtering')}
                      tags={uasSecurityTags}
                      onChange={handleChangeSecurityTags}
                      noneAddedText=""
                      placeholder={t('general.search')}
                    />
                  </FormGroup>
                </DynamicContainer>
              </>
            )}
          </FormSection>
        </>
      )}
      <FormSection className="hai-mb-6" title={t('metadata.settings.missionIdInsertion')}>
        <DynamicContainer minColumns={3}>
          <FormTextfield name="missionId" mask title={t('metadata.settings.missionId')} />
        </DynamicContainer>
      </FormSection>
      <FormSection title={t('metadata.settings.securityDataInsertion')}>
        <DynamicContainer>
          <FormSwitch
            className="hai-mb-4"
            name="securityInsertionEnabled"
            switchLabel={t('metadata.settings.securityDataInsertion')}
          />
        </DynamicContainer>
        {values.securityInsertionEnabled && (
          <>
            <DynamicContainer maxColumns={3}>
              <FormSelect
                name="securityClassification"
                title={t('metadata.settings.securityClassification')}
                options={securityClassificationOptions}
              />
              <FormSelect
                name="codingMethod"
                title={t('metadata.settings.countryCodingMethod')}
                options={countryCodingOptions}
              />
              <FormTextfield
                inputClassName="uppercase"
                mask
                name="classifyingCountry"
                title={t('metadata.settings.classifyingCountry')}
                maxLength="3"
                required
              />
              <FormTextfield
                inputClassName="uppercase"
                mask
                name="countryCodes"
                title={t('metadata.settings.countryCodes')}
                hint={t('metadata.settings.countryCodesExplain')}
                required
              />
            </DynamicContainer>
          </>
        )}
      </FormSection>
    </>
  );
};
